import React from 'react';
import { get, isEmpty } from 'lodash';
import { Image } from 'react-native';
import { Chip } from 'react-native-paper';
import { IChipListV4 } from '@nocode/types';
import { defaultURL } from '../../../../utils/common.constant';
import {
  getActions,
  getImageCardCover,
  getValueBinding,
  isCheckColor,
} from '../../../shared';
import createStyles from './style';
import { useImageSecurity } from '@common/components/ImageSecurity';

type Props = {
  attrs: IChipListV4;
  active: boolean;
  index: number;
  item: any;
  onPress?: any;
};

const ChipAvatar = ({
  attrs,
  item,
  style,
}: Pick<Props, 'attrs' | 'item'> & {
  style: ReturnType<typeof createStyles>['avatar'];
}) => {
  const { cardCover } = attrs.attributes;

  const placeholderImageUrl = get(
    attrs,
    'attributes.cardCover.image.binding.options.placeholderImage',
    null
  );

  const placeholderImageEnabled = get(
    attrs,
    'attributes.cardCover.image.binding.options.placeholderImageEnabled',
    false
  );

  const imageType = get(cardCover, 'image.imageType');
  const placeholderImage =
    placeholderImageEnabled && imageType !== 'uploaded'
      ? placeholderImageUrl
      : null;

  const imageURL = getImageCardCover(item, cardCover);
  const placeholderUrl = placeholderImageEnabled
    ? placeholderImage
    : defaultURL;
  const cardCoverImage = !isEmpty(imageURL) ? imageURL : placeholderUrl;
  const { imageUrl: secureCardCover } = useImageSecurity(cardCoverImage);

  return (
    <Image
      nativeID="avatar"
      style={style}
      defaultSource={{ uri: placeholderUrl }}
      source={{ uri: secureCardCover }}
    />
  );
};

const ChipItem = ({ attrs, index, item, onPress, active }: Props) => {
  const style = createStyles(attrs);
  const {
    closeIcon = { enabled: false },
    title = { enabled: false },
    cardCover = { enabled: false },
  } = attrs.attributes;

  const chipStyle = attrs.style.tag ?? {};
  const titleStyleAttrs = attrs.attributes.title?.style ?? {};
  const closeIconStyle = attrs.attributes.closeIcon?.style ?? {};

  const bindingValue = getValueBinding('', item, attrs);

  const selectedBgColor = isCheckColor(
    chipStyle.selected?.backgroundColor as string
  )
    ? chipStyle.selected.backgroundColor
    : '#0175FF';

  const selectedTextColor = isCheckColor(
    titleStyleAttrs.selected?.color as string
  )
    ? titleStyleAttrs.selected.color
    : '#ffffff';

  const chipItemStyle = [
    style.chip,
    active && { backgroundColor: selectedBgColor },
  ];

  const textStyle = [
    style.text,
    active && { color: selectedTextColor },
    {
      minHeight: Math.max(
        titleStyleAttrs.lineHeight ?? 0,
        titleStyleAttrs.fontSize ?? 0
      ),
    },
  ];

  return (
    <Chip
      {...(closeIcon.enabled && closeIcon.icon !== ''
        ? {
            closeIcon: closeIcon.icon,
            onClose: () => {
              onPress &&
                onPress(getActions(attrs, 'closeIcon'), {
                  record: item,
                  indexRecord: index,
                });
            },
            selectedColor: isCheckColor(closeIconStyle.iconColor)
              ? closeIconStyle.iconColor
              : '#000000',
          }
        : {})}
      onPress={() =>
        onPress &&
        onPress(getActions(attrs, 'onPress'), {
          record: item,
          indexRecord: index,
        })
      }
      style={chipItemStyle}
      textStyle={textStyle}
      avatar={
        cardCover.enabled ? (
          <ChipAvatar attrs={attrs} item={item} style={style.avatar} />
        ) : undefined
      }
      mode={chipStyle.borderWidth === 0 ? 'flat' : 'outlined'}
    >
      {title.enabled
        ? item['title.text'] || get(bindingValue, 'title.text')
        : ''}
    </Chip>
  );
};

export default ChipItem;
